import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import logo from './assets/logo.svg';
import { getDefaultPets, mergePets } from './data/cataloguePets';
import { getProcessSteps, getWhyForYou, REVIEWS } from './data/marketing';
import { loadCart, loadPets, saveCart, savePets } from './utils/storage';
import NavBar from './components/NavBar';
import Footer from './components/Footer';
import FloatingActions from './components/FloatingActions';
import CartDrawer from './components/CartDrawer';
import HomePage from './pages/HomePage';
import CataloguePage from './pages/CataloguePage';
import ReviewsPage from './pages/ReviewsPage';
import GalleryPage from './pages/GalleryPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import AdminPage from './pages/AdminPage';
import PupPage from './pages/PupPage';
import { fetchPups, pupsStoreEnabled } from './services/pupsStore';

const isUnavailableStatus = (status) => {
  const s = String(status || '').trim().toLowerCase();
  return s === 'sold' || s === 'not available' || s === 'unavailable' || s === 'out of stock';
};

export default function App() {
  const brand = "Mary's Plush Pups";
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const remoteEnabled = pupsStoreEnabled();

  const contact = useMemo(
    () => ({
      name: "Mary's Plush Pups",
      location: 'USA',
      email: 'marynerusambani39@gmail.com',
      whatsappUrl: 'https://wa.me/15189525889',
      facebookUrl: 'https://www.facebook.com/share/1HahQfohiQ/?mibextid=wwXIfr'
    }),
    []
  );

  const allPupImages = useMemo(
    () =>
      Array.from({ length: 15 }).map(
        (_, idx) => `/images/pups/pup-${String(idx + 1).padStart(2, '0')}.jpeg`
      ),
    []
  );

  const heroPups = useMemo(() => {
    const list = [...allPupImages];
    for (let i = list.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [list[i], list[j]] = [list[j], list[i]];
    }
    return list.slice(0, 4);
  }, [allPupImages]);

  const heroMainImage = heroPups[0];
  const heroFloating = heroPups.slice(1);

  const [pets, setPets] = useState(() => {
    if (remoteEnabled) return [];
    const defaults = getDefaultPets();
    const stored = loadPets();
    return stored ? mergePets(stored, defaults) : defaults;
  });

  const [cartPetIds, setCartPetIds] = useState(() => loadCart());
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cartName, setCartName] = useState('');
  const [cartLocation, setCartLocation] = useState('');
  const [cartNotes, setCartNotes] = useState('');

  const whyForYou = useMemo(() => getWhyForYou(allPupImages), [allPupImages]);
  const processSteps = useMemo(() => getProcessSteps(allPupImages), [allPupImages]);
  const reviews = useMemo(() => REVIEWS, []);

  useEffect(() => {
    if (remoteEnabled) return;
    savePets(pets);
  }, [pets, remoteEnabled]);
  useEffect(() => saveCart(cartPetIds), [cartPetIds]);

  const onPetUpdated = (petId, nextPet) => {
    if (!nextPet) return;
    setPets((prev) => prev.map((p) => (p.id === petId ? { ...p, ...nextPet } : p)));
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!remoteEnabled) return;
      if (location.pathname.startsWith('/admin')) return;
      try {
        const remote = await fetchPups();
        if (cancelled) return;
        if (Array.isArray(remote)) setPets(remote);
      } catch (e) {
        // Keep local fallback if Supabase is unavailable.
        // eslint-disable-next-line no-console
        console.warn('Failed to fetch pups from Supabase:', e);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, remoteEnabled]);

  const openShippingWhatsApp = () => {
    const text =
      "Hi Mary's Plush Pups! I'm interested in your pups. Please share more information about the shipping/delivery process and costs.";
    const url = `${contact.whatsappUrl}?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'noreferrer');
  };

  const openGeneralWhatsApp = () => {
    const text = "Hi Mary's Plush Pups! I’d like to enquire about available pups.";
    const url = `${contact.whatsappUrl}?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'noreferrer');
  };

  const cartPets = useMemo(
    () => cartPetIds.map((id) => pets.find((p) => p.id === id)).filter(Boolean),
    [cartPetIds, pets]
  );

  const toggleCart = () => setIsCartOpen((v) => !v);

  const addToCart = (petId) => () => {
    const pet = pets.find((p) => p.id === petId);
    if (pet && isUnavailableStatus(pet.status)) return;
    setCartPetIds((prev) => (prev.includes(petId) ? prev : [...prev, petId]));
    setIsCartOpen(true);
  };

  const removeFromCart = (petId) => () => {
    setCartPetIds((prev) => prev.filter((id) => id !== petId));
  };

  useEffect(() => {
    setCartPetIds((prev) =>
      prev.filter((id) => {
        const pet = pets.find((p) => p.id === id);
        return pet && !isUnavailableStatus(pet.status);
      })
    );
  }, [pets]);

  const clearCart = () => setCartPetIds([]);

  const checkoutToWhatsApp = () => {
    if (!cartPets.length) return;
    const lines = [`Hi ${brand}!`, '', 'I’d like to enquire about these pups:'];

    for (const p of cartPets) {
      const details = [p.age, p.size, p.weight].filter((x) => x && x !== 'Ask for details').join(' • ');
      const priceText = typeof p.price === 'number' ? `$${p.price}` : '';
      lines.push(`- ${p.title}${priceText ? ` (${priceText})` : ''}${details ? ` — ${details}` : ''}`);
    }

    const total = cartPets.reduce((sum, p) => sum + (typeof p.price === 'number' ? p.price : 0), 0);
    if (total) lines.push('', `Estimated total: $${total}`);
    if (cartName.trim()) lines.push('', `My name: ${cartName.trim()}`);
    if (cartLocation.trim()) lines.push(`My location: ${cartLocation.trim()}`);
    if (cartNotes.trim()) lines.push('', `Notes: ${cartNotes.trim()}`);
    lines.push('', 'Please confirm availability and share next steps (pickup/shipping).');

    const url = `${contact.whatsappUrl}?text=${encodeURIComponent(lines.join('\n'))}`;
    window.open(url, '_blank', 'noreferrer');
  };

  const onBackToTop = () => {
    if (location.pathname !== '/') {
      navigate('/');
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="App">
      {!isAdminRoute ? <NavBar brand={brand} logo={logo} showManageLink={false} /> : null}

      <Routes>
        <Route
          path="/"
          element={
            <HomePage
              heroMainImage={heroMainImage}
              heroFloating={heroFloating}
              contact={contact}
              whyForYou={whyForYou}
              pets={pets}
              onViewCatalogue={() => navigate('/catalogue')}
            />
          }
        />
        <Route path="/catalogue" element={<CataloguePage pets={pets} onAddToCart={addToCart} whatsappUrl={contact.whatsappUrl} />} />
        <Route
          path="/pup/:id"
          element={
            <PupPage
              brand={brand}
              pets={pets}
              contact={contact}
              cartPetIds={cartPetIds}
              onAddToCart={addToCart}
              onRemoveFromCart={removeFromCart}
              onPetUpdated={onPetUpdated}
            />
          }
        />
        <Route path="/reviews" element={<ReviewsPage reviews={reviews} onMessageWhatsApp={openGeneralWhatsApp} />} />
        <Route path="/gallery" element={<GalleryPage images={allPupImages} />} />
        <Route path="/past-pups" element={<Navigate to="/gallery" replace />} />
        <Route
          path="/about"
          element={
            <AboutPage
              brand={brand}
              processSteps={processSteps}
              onShippingWhatsApp={openShippingWhatsApp}
              onViewCatalogue={() => navigate('/catalogue')}
            />
          }
        />
        <Route path="/contact" element={<ContactPage contact={contact} />} />
        <Route path="/admin" element={<AdminPage brand={brand} logo={logo} defaultPets={getDefaultPets()} />} />
        <Route path="/manage" element={<Navigate to="/admin" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {!isAdminRoute ? <Footer brand={brand} onBackToTop={onBackToTop} /> : null}

      {!isAdminRoute ? (
        <CartDrawer
          isOpen={isCartOpen}
          onClose={toggleCart}
          cartPets={cartPets}
          cartName={cartName}
          setCartName={setCartName}
          cartLocation={cartLocation}
          setCartLocation={setCartLocation}
          cartNotes={cartNotes}
          setCartNotes={setCartNotes}
          onRemoveFromCart={removeFromCart}
          onClearCart={clearCart}
          onCheckoutToWhatsApp={checkoutToWhatsApp}
        />
      ) : null}

      {!isAdminRoute ? (
        <FloatingActions cartCount={cartPetIds.length} onOpenCart={toggleCart} onWhatsApp={openGeneralWhatsApp} />
      ) : null}
    </div>
  );
}
